import { forwardRef, type HTMLAttributes } from 'react';
import styles from './Tab.module.css';

type TrackingValue = string | number | boolean;

export interface TabProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  href?: string;
  className?: string;
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
}

export const Tab = forwardRef<HTMLButtonElement | HTMLAnchorElement, TabProps>(
  function Tab(
    {
      label,
      isActive = false,
      onClick,
      href,
      trackEvent,
      trackData,
      className,
      ...props
    },
    ref
  ) {
    const classes = [
      styles.tab,
      isActive && styles.active,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const Comp = href ? 'a' : 'button';

    const trackingAttributes: Record<string, string> = {};
    if (trackEvent) {
      trackingAttributes['data-track'] = trackEvent;

      if (trackData) {
        for (const [key, value] of Object.entries(trackData)) {
          if (value == null) continue;
          const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
          trackingAttributes[attrKey] = String(value);
        }
      }
    }

    return (
      <Comp
        ref={ref as any}
        className={classes}
        href={href}
        onClick={onClick}
        role="tab"
        aria-selected={isActive}
        aria-current={isActive ? 'page' : undefined}
        {...(Comp === 'button' ? { type: 'button' } : {})}
        {...trackingAttributes}
        {...props}
      >
        <span className={styles.label}>{label}</span>
        {isActive && <span className={styles.indicator} aria-hidden="true" />}
      </Comp>
    );
  }
);

Tab.displayName = 'Tab';
